import { useEffect, useState, Fragment } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import axios from 'axios'
import toast from 'react-hot-toast'

function PaymentConfirmation() {
   const location = useLocation();
   const navigate = useNavigate();

   const queryParams = new URLSearchParams(location.search);
   const reference = queryParams.get('reference') || '';
   const microcreditId = queryParams.get('microcredit_id') || '';

   const [checking, setChecking] = useState(true);
   const [authenticated, setAuthenticated] = useState(false);
   const [checkoutUrl, setCheckoutUrl] = useState('');
   const [timeoutReached, setTimeoutReached] = useState(false);

   useEffect(() => {
      if (reference) {
         checkPayment();
      } else {
         setChecking(false);
      }
   }, [reference]);

   const timeoutMs = 120000;
   const pollIntervalMs = 5000;

   async function checkPayment() {
      const startTime = Date.now();

      const poll = async () => {
         try {
            const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/api/microcredit-payment/check`, {
               params: { reference, microcredit_id: microcreditId },
               headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
            });

            if (response.data.checkoutUrl) {
               setCheckoutUrl(response.data.checkoutUrl);
            }

            if (response.data.status === 'active') {
               if (response.data.token && response.data.refreshToken) {
                  localStorage.setItem('token', response.data.token);
                  localStorage.setItem('refresh-token', response.data.refreshToken);
               }
               setAuthenticated(true);
               setChecking(false);
               return;
            }

            if (response.data.status === 'failed') {
               toast.error('O pagamento falhou.');
               setChecking(false);
               return;
            }

            const elapsed = Date.now() - startTime;
            if (elapsed >= timeoutMs) {
               setTimeoutReached(true);
               setChecking(false);
               toast('Tempo de espera esgotado. Clique no botão para retomar o pagamento.', { icon: '⏱️' });
               return;
            }

            setTimeout(() => poll(), pollIntervalMs);
         } catch (e) {
            toast.error('Erro ao verificar status do pagamento.');
            setChecking(false);
         }
      };

      setChecking(true);
      poll();
   }

   function goToLogin() {
      window.location.href = '/login';
   }

   function goToDashboard() {
      window.location.href = '/';
   }

   function openCheckout() {
      if (checkoutUrl) {
         window.open(checkoutUrl, '_blank', 'noopener,noreferrer');
      }
   }

   return (
      <div className="min-h-screen bg-gradient-to-br from-custom_blue-50 to-custom_blue-100 flex items-center justify-center py-12 px-6">
         <div className="bg-white rounded-3xl shadow-2xl max-w-lg w-full p-10 text-center">
            {checking ? (
               <Fragment>
                  <div className="w-16 h-16 border-4 border-custom_blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
                  <h2 className="text-2xl font-bold text-custom_blue-700 mb-4">Verificando pagamento</h2>
                  <p className="text-gray-600 mb-6">
                     Estamos verificando o status do seu pagamento...
                  </p>
                  <p className="text-sm text-gray-500">
                     Esta página verificará automaticamente o status do pagamento a cada 5 segundos.
                  </p>
               </Fragment>
            ) : authenticated ? (
               <Fragment>
                  <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                     <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 -960 960 960" width="48" className="fill-green-500">
                        <path d="m424-296 282-282-56-56-226 226-114-114-56 56 170 170Zm56 216q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Zm0-80q134 0 227-93t93-227q0-134-93-227t-227-93q-134 0-227 93t-93 227q0 134 93 227t227 93Zm0-320Z" />
                     </svg>
                  </div>
                  <h1 className="text-3xl font-bold text-custom_blue-700 mb-4">
                     Pagamento Confirmado!
                  </h1>
                  <p className="text-gray-600 mb-8">
                     O seu microcrédito foi activado com sucesso e a sua conta já está pronta a usar.
                     <br />
                     Tudo foi configurado para que possa começar imediatamente.
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3 justify-center">
                     <button
                        onClick={goToDashboard}
                        className="px-8 py-3 bg-custom_blue-600 text-white font-semibold rounded-xl hover:bg-custom_blue-700 transition-all duration-200 flex items-center justify-center gap-2"
                     >
                        Ir para o Dashboard
                     </button>
                     <button
                        onClick={goToLogin}
                        className="px-8 py-3 border-2 border-custom_blue-600 text-custom_blue-600 font-semibold rounded-xl hover:bg-custom_blue-50 transition-all duration-200"
                     >
                        Ir para Login
                     </button>
                  </div>
               </Fragment>
            ) : timeoutReached ? (
               <Fragment>
                  <div className="w-16 h-16 rounded-full bg-yellow-100 flex items-center justify-center mx-auto mb-6">
                     <svg xmlns="http://www.w3.org/2000/svg" height="32" viewBox="0 -960 960 960" width="32">
                        <path d="M480-80q-83 0-156-31.5T197-197q-54-54-85.5-127T80-480q0-83 31.5-156T197-763q54-54 127-85.5T480-880q83 0 156 31.5T763-763q54 54 85.5 127T880-480q0 83-31.5 156T763-197q-54 54-127 85.5T480-80Zm0-80q134 0 227-93t93-227q0-134-93-227t-227-93q-134 0-227 93t-93 227q0 134 93 227t227 93Zm-1-160v-120h-80v120h-120v80h120v120h80v-120h120v-80h-120Z" className="fill-yellow-500" />
                     </svg>
                  </div>
                  <h2 className="text-xl font-bold text-gray-700 mb-4">Pagamento pendente</h2>
                  <p className="text-gray-600 mb-6">
                     O tempo de verificação foi esgotado. Se você completou o pagamento, aguarde alguns minutos
                     e clique em "Verificar novamente" ou use o botão abaixo para retomar o pagamento.
                  </p>
                  {checkoutUrl && (
                     <div className="mb-4">
                        <button
                           onClick={openCheckout}
                           className="w-full px-6 py-3 bg-custom_blue-600 text-white font-semibold rounded-xl hover:bg-custom_blue-700 transition-all duration-200 flex items-center justify-center gap-2"
                        >
                           <svg xmlns="http://www.w3.org/2000/svg" height="20" viewBox="0 -960 960 960" width="20">
                              <path d="M180-120v-560h720v560H180Zm90-80h80v-80h-80v80Zm220 0h80v-80h-80v80Zm140 0h80v-80h-80v80Zm140 0h80v-80h-80v80Z" />
                           </svg>
                           Abrir página de pagamento
                        </button>
                     </div>
                  )}
                  <button
                     onClick={() => {
                        setTimeoutReached(false);
                        setChecking(true);
                        checkPayment();
                     }}
                     className="w-full px-6 py-3 border-2 border-custom_blue-600 text-custom_blue-600 font-semibold rounded-xl hover:bg-custom_blue-50 transition-all duration-200"
                  >
                     Verificar novamente
                  </button>
               </Fragment>
            ) : (
               <Fragment>
                  <div className="w-16 h-16 rounded-full bg-red-100 flex items-center justify-center mx-auto mb-6">
                     <svg xmlns="http://www.w3.org/2000/svg" height="32" viewBox="0 -960 960 960" width="32">
                        <path d="m342-358 114-114-114-114 57-57 114 114 114-114 57 57-114 114 114 114-57 57-114-114-114 114Z" className="fill-red-500" />
                     </svg>
                  </div>
                  <h2 className="text-xl font-bold text-gray-700 mb-4">Pagamento não confirmado</h2>
                  <p className="text-gray-600 mb-6">
                     Não conseguimos confirmar seu pagamento. Tente novamente ou entre em contato conosco.
                  </p>
                  <button
                     onClick={() => navigate('/')}
                     className="px-8 py-3 bg-custom_blue-600 text-white font-semibold rounded-xl hover:bg-custom_blue-700 transition-all duration-200"
                  >
                     Voltar
                  </button>
               </Fragment>
            )}
         </div>
      </div>
   )
}

export default PaymentConfirmation;
